<template>
  <div class="dark-panel">
    <v-row class="ma-0" align="start">
      <!-- Left: Filters -->
      <v-col cols="12" md="3" class="pa-2">
        <label class="panel-label">{{ $t('Unidade') }}</label>
        <v-autocomplete
          hide-details
          v-model="chartFilter.unidadeId"
          :items="unidades"
          item-value="id"
          item-text="name"
          :loading="unidadeLoading"
          :placeholder="$t('Todas')"
          outlined
          dense
          dark
          clearable
          class="panel-input"
        />

        <label class="panel-label mt-3">{{ $t('Período') }}</label>
        <div class="d-flex align-center" style="gap: 6px">
          <v-menu
            ref="startMenu"
            :close-on-content-click="false"
            :return-value.sync="chartFilter.startDate"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatMonthShort(chartFilter.startDate)"
                readonly outlined dense dark hide-details
                class="panel-input" v-bind="attrs" v-on="on"
              />
            </template>
            <v-date-picker
              v-model="chartFilter.startDate"
              type="month"
              :locale="$i18n.locale"
              no-title
              scrollable
              @change="$refs.startMenu.save(chartFilter.startDate)"
            />
          </v-menu>

          <span class="white--text" style="font-size: 12px; white-space: nowrap">{{ $t('até') }}</span>

          <v-menu
            ref="endMenu"
            :close-on-content-click="false"
            :return-value.sync="chartFilter.endDate"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatMonthShort(chartFilter.endDate)"
                readonly outlined dense dark hide-details
                class="panel-input" v-bind="attrs" v-on="on"
              />
            </template>
            <v-date-picker
              v-model="chartFilter.endDate"
              type="month"
              :locale="$i18n.locale"
              no-title
              scrollable
              @change="$refs.endMenu.save(chartFilter.endDate)"
            />
          </v-menu>
        </div>

        <div class="d-flex mt-3" style="gap: 8px">
          <v-btn
            small depressed color="#bbcf20"
            class="flex-grow-1"
            style="color: #0f3565; font-weight: 600"
            @click="applyFilter"
          >
            <v-icon small left>mdi-magnify</v-icon>
            {{ $t('Filtrar') }}
          </v-btn>
          <v-btn small depressed outlined dark class="flex-grow-1" @click="resetFilter">
            <v-icon small left>mdi-cached</v-icon>
            {{ $t('Limpar') }}
          </v-btn>
        </div>
      </v-col>

      <!-- Right: Charts -->
      <v-col cols="12" md="9" class="pa-2">
        <v-row class="ma-0" style="gap: 0">
          <v-col cols="12" class="chart-card mb-4">
            <EquipmentByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <CalledsByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <ServiceOrdersRequestedByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <ServiceOrdersClosedByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <TeamResolutivityByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <CalledModuleAdhesionByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <PreventiveCompletionByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <ServiceOrdersByResponsible :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <AvgDaysOpenByMonth :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card mb-4">
            <ServiceOrdersOpenedByDay :filterProp="appliedFilter" />
          </v-col>
          <v-col cols="12" class="chart-card">
            <ServiceOrdersClosedByDay :filterProp="appliedFilter" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment'
import ApiService from '../../../services/ApiService'
import EquipmentByMonth from './components/EquipmentByMonth.vue'
import CalledsByMonth from './components/CalledsByMonth.vue'
import ServiceOrdersRequestedByMonth from './components/ServiceOrdersRequestedByMonth.vue'
import ServiceOrdersClosedByMonth from './components/ServiceOrdersClosedByMonth.vue'
import TeamResolutivityByMonth from './components/TeamResolutivityByMonth.vue'
import CalledModuleAdhesionByMonth from './components/CalledModuleAdhesionByMonth.vue'
import PreventiveCompletionByMonth from './components/PreventiveCompletionByMonth.vue'
import ServiceOrdersByResponsible from './components/ServiceOrdersByResponsible.vue'
import AvgDaysOpenByMonth from './components/AvgDaysOpenByMonth.vue'
import ServiceOrdersOpenedByDay from './components/ServiceOrdersOpenedByDay.vue'
import ServiceOrdersClosedByDay from './components/ServiceOrdersClosedByDay.vue'

export default {
  components: { EquipmentByMonth, CalledsByMonth, ServiceOrdersRequestedByMonth, ServiceOrdersClosedByMonth, TeamResolutivityByMonth, CalledModuleAdhesionByMonth, PreventiveCompletionByMonth, ServiceOrdersByResponsible, AvgDaysOpenByMonth, ServiceOrdersOpenedByDay, ServiceOrdersClosedByDay },

  data() {
    const institution = JSON.parse(localStorage.getItem('institution'))
    const defaultStart = moment().subtract(12, 'months').format('YYYY-MM')
    const defaultEnd = moment().format('YYYY-MM')

    return {
      institution,
      chartFilter: {
        unidadeId: null,
        startDate: defaultStart,
        endDate: defaultEnd,
      },
      appliedFilter: {
        institutionId: institution ? institution.id : null,
        unidadeId: null,
        startDate: moment(defaultStart).startOf('month').format('YYYY-MM-DD'),
        endDate: moment(defaultEnd).endOf('month').format('YYYY-MM-DD'),
      },
      unidades: [],
      unidadeLoading: false,
      apiService: new ApiService(),
    }
  },

  created() {
    this.getUnidades()
  },

  methods: {
    async getUnidades() {
      if (!this.institution) return
      this.unidadeLoading = true
      try {
        const response = await this.apiService.get(
          `unidade/listByUserAndInstitution/${this.institution.id}`
        )
        this.unidades = response.content
      } catch (err) {
        // errors handled globally by ApiService
      } finally {
        this.unidadeLoading = false
      }
    },

    applyFilter() {
      this.appliedFilter = {
        institutionId: this.institution ? this.institution.id : null,
        unidadeId: this.chartFilter.unidadeId || null,
        startDate: moment(this.chartFilter.startDate)
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: moment(this.chartFilter.endDate)
          .endOf('month')
          .format('YYYY-MM-DD'),
      }
    },

    resetFilter() {
      const defaultStart = moment().subtract(12, 'months').format('YYYY-MM')
      const defaultEnd = moment().format('YYYY-MM')
      this.chartFilter = { unidadeId: null, startDate: defaultStart, endDate: defaultEnd }
      this.applyFilter()
    },

    formatMonthShort(date) {
      return moment(date).format('MM/YYYY')
    },
  },
}
</script>

<style scoped>
/* ── Dark panel ── */
.dark-panel {
  background: linear-gradient(160deg, #0a2a4a 0%, #0f3565 40%, #122e50 100%);
  border-radius: 0 0 12px 12px;
  padding: 20px;
  min-height: 300px;
  position: relative;
  overflow: hidden;
}

/* ── Filter inputs ── */
.panel-label {
  display: block;
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.panel-input >>> .v-input__slot {
  min-height: 36px !important;
  height: 36px !important;
  font-size: 13px;
  align-items: center;
  padding: 0 10px !important;
}
.panel-input >>> .v-select__selections {
  min-height: 36px !important;
  padding: 2px 0 !important;
  align-items: center;
}
.panel-input >>> input {
  font-size: 13px;
  padding: 4px 0 !important;
  max-height: 36px;
}
.panel-input >>> .v-input__append-inner,
.panel-input >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center;
}
.panel-input >>> .v-input__append-inner .v-icon,
.panel-input >>> .v-input__prepend-inner .v-icon {
  font-size: 18px;
}
.panel-input >>> fieldset {
  border-color: rgba(255, 255, 255, 0.2) !important;
  transition: border-color 0.2s;
}
.panel-input >>> .v-input__slot:hover fieldset {
  border-color: rgba(255, 255, 255, 0.45) !important;
}
.panel-input >>> .v-input--is-focused fieldset,
.panel-input.v-input--is-focused >>> fieldset {
  border-color: #bbcf20 !important;
  border-width: 1px !important;
}

/* ── Chart cards ── */
.chart-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 16px 20px;
}
</style>
